import React, { useState, useEffect } from 'react';
import { ToastProvider } from './hooks/useToast';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { DashboardLayout } from './layouts/DashboardLayout';
import { DashboardOverview } from './pages/DashboardOverview';
import { LinksList } from './pages/LinksList';
import { LinkEditor } from './pages/LinkEditor';
import { CampaignsList } from './pages/CampaignsList';
import { CampaignDetail } from './pages/CampaignDetail';
import { AnalyticsPage } from './pages/AnalyticsPage';
import { SettingsPage } from './pages/SettingsPage';
import { PublicLanding } from './pages/PublicLanding';
import { LoginPage } from './pages/LoginPage';
import { SetupPasswordPage } from './pages/SetupPasswordPage';

export const AppContent: React.FC = () => {
  const { user, isLoading } = useAuth();
  const [currentPath, setCurrentPath] = useState<string>(() => {
    return window.location.pathname || '/';
  });

  // Listen to browser navigation (back/forward)
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname || '/');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = (path: string) => {
    if (window.location.pathname !== path) {
      window.history.pushState({}, '', path);
      setCurrentPath(path);
      window.scrollTo(0, 0);
    }
  };

  // 1a. Explicit /login route
  if (currentPath === '/login') {
    if (!isLoading && user) {
      navigate('/admin');
      return null;
    }
    return <LoginPage onSuccess={() => navigate('/admin')} />;
  }

  // 1b. One-time invitation password setup route
  if (currentPath === '/setup-password' || currentPath === '/invite') {
    return (
      <SetupPasswordPage
        onSuccess={() => navigate('/admin')}
        onNavigateLogin={() => navigate('/login')}
      />
    );
  }

  // 2. Public landing page at /
  if (currentPath === '/' || currentPath === '') {
    return <PublicLanding onEnterDashboard={() => navigate('/admin')} />;
  }

  // 3. Admin routes require authentication
  if (currentPath.startsWith('/admin')) {
    if (isLoading) {
      return (
        <div className="min-h-screen bg-[#090d16] flex items-center justify-center text-slate-400 text-sm">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <span>Verifying staff session...</span>
          </div>
        </div>
      );
    }

    if (!user) {
      return <LoginPage onSuccess={() => navigate(currentPath)} />;
    }
  }

  // Determine current active view inside administrative dashboard
  const renderDashboardView = () => {
    // 1. /admin/links/new
    if (currentPath === '/admin/links/new') {
      return <LinkEditor onNavigate={navigate} />;
    }

    // 2. /admin/links/:id
    if (currentPath.startsWith('/admin/links/') && currentPath !== '/admin/links') {
      const linkId = currentPath.replace('/admin/links/', '');
      return <LinkEditor linkId={linkId} onNavigate={navigate} />;
    }

    // 3. /admin/links
    if (currentPath === '/admin/links') {
      return (
        <LinksList
          onNavigate={navigate}
          onOpenCreate={() => navigate('/admin/links/new')}
          onEditLink={(id) => navigate(`/admin/links/${id}`)}
        />
      );
    }

    // 4. /admin/campaigns/:id
    if (currentPath.startsWith('/admin/campaigns/') && currentPath !== '/admin/campaigns') {
      const campaignId = currentPath.replace('/admin/campaigns/', '');
      return (
        <CampaignDetail
          campaignId={campaignId}
          onNavigate={navigate}
          onOpenCreate={() => navigate('/admin/links/new')}
          onEditLink={(id) => navigate(`/admin/links/${id}`)}
        />
      );
    }

    // 5. /admin/campaigns
    if (currentPath === '/admin/campaigns') {
      return (
        <CampaignsList
          onNavigate={navigate}
          onSelectCampaign={(id) => navigate(`/admin/campaigns/${id}`)}
        />
      );
    }

    // 6. /admin/analytics
    if (currentPath === '/admin/analytics') {
      return <AnalyticsPage />;
    }

    // 7. /admin/settings
    if (currentPath === '/admin/settings') {
      return <SettingsPage />;
    }

    // Default /admin -> Overview
    return (
      <DashboardOverview
        onNavigate={navigate}
        onOpenCreate={() => navigate('/admin/links/new')}
      />
    );
  };

  return (
    <DashboardLayout activePath={currentPath} onNavigate={navigate}>
      {renderDashboardView()}
    </DashboardLayout>
  );
};

export const App: React.FC = () => {
  return (
    <ToastProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ToastProvider>
  );
};

export default App;
